import classnames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import finishedLearningItems from '../../state/calculations/finishedLearningItems'
import learningItemTimeInvested from '../../state/calculations/learningItemTimeInvested'
import { useUser } from '../../state/entities'
import useUserLearningItems from '../../state/queries/useUserLearningItems'
import useRoutes from '../../state/useRoutes'

import styles from './InsightsPage.module.scss'

/**********************************************************************
 * InsightsPageFinishedLearningItems
 **********************************************************************/
export const InsightsPageFinishedLearningItems = React.memo(function InsightsPageFinishedLearningItems({
  userUuid,
}: {
  userUuid?: string
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  const { user } = useUser(userUuid)

  const { userLearningItems } = useUserLearningItems(userUuid)

  const finishedItems = useMemo(() => finishedLearningItems(userLearningItems), [userLearningItems])

  return (
    <section className={styles.InsightsPageFinishedLearningItems}>
      <header>
        <h1>{t('finished learning items')}</h1>
      </header>

      <main className={classnames({ skeleton: user.isDummy })}>
        <table>
          <thead>
            <tr>
              <th className={styles.nameColumn}>{t('title')}</th>
              <th className={styles.startedAtColumn}>{t('learning assignment started at')}</th>
              <th className={styles.finishedAtColumn}>{t('learning assignment finished at')}</th>
              <th className={styles.timeSpentColumn}>{t('learning item.time spent')}</th>
              <th className={styles.priceColumn}>{t('learning item.price')}</th>
            </tr>
          </thead>
          <tbody>
            {finishedItems.map(({ learningItem }) => (
              <tr key={learningItem.uuid}>
                <td className={styles.nameColumn}>
                  <span className={styles.columnDescription}>{t('title')}</span>
                  <span className={styles.columnContent}>
                    <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
                  </span>
                </td>
                <td className={styles.startedAtColumn}>
                  <span className={styles.columnDescription}>{t('learning assignment started at')}</span>
                  {t('format.DATE', { date: learningItem.learningProgress?.firstAccessedAt })}
                </td>
                <td className={styles.finishedAtColumn}>
                  <span className={styles.columnDescription}>{t('learning assignment finished at')}</span>
                  {t('format.DATE', { date: learningItem.learningProgress?.completedAt })}
                </td>
                <td className={styles.timeSpentColumn}>
                  <span className={styles.columnDescription}>{t('learning item.time spent')}</span>
                  {t('format.DURATION', {
                    duration: { seconds: learningItemTimeInvested(learningItem) },
                  })}
                </td>
                <td className={styles.priceColumn}>
                  <span className={styles.columnDescription}>{t('learning item.price')}</span>
                  {t('learning item.included')}
                </td>
              </tr>
            ))}
            {!finishedItems.length && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  {t('no finished learning items found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </main>
    </section>
  )
})
